import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, IconButton,
  Paper, Stack, TextField, Typography, Avatar, Tooltip,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Cancel as RejectIcon,
  Description as DocIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircleOutline as CheckedIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AccessTime as TimeIcon,
  PersonOutline as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  RequestPage as RequestIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { themeColors } from '../app/layout/theme'
import { customerVerificationApi } from '../lib/api/customerVerificationApi'
import type { CustomerVerificationStatus, VerificationNote } from '../types/verificationTypes'
import { maskPhoneNumber } from '../utils/phoneUtils'

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { bg: string; color: string; border: string }> = {
  'Pending':             { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'In Review':          { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Approved':           { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Rejected':           { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  'Need More Documents':{ bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return (
    <Chip label={status} size="small"
      sx={{ height: 24, borderRadius: '6px', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '12px', fontWeight: 700, '& .MuiChip-label': { px: '10px' } }} />
  )
}

function initials(name: string) { return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('') }
const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) { let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h); return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length] }
function fmt(iso: string | null | undefined) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) }
function fmtTime(iso: string) { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }

// ── Info Row ──────────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, borderBottom: `1px solid ${themeColors.borderLight}`, '&:last-child': { borderBottom: 'none', pb: 0 } }}>
      <Box sx={{ color: themeColors.text.muted, pt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, fontWeight: 500, mb: 0.25 }}>{label}</Typography>
        <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</Typography>
      </Box>
    </Box>
  )
}

// ── Card ──────────────────────────────────────────────────────────────────────
function SectionCard({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${themeColors.border}`, bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color: themeColors.primary, display: 'flex' }}>{icon}</Box>}
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Paper>
  )
}

// ── Timeline Icon ─────────────────────────────────────────────────────────────
const TL_ICONS: Record<string, { color: string; bg: string }> = {
  created:   { color: '#4B3082', bg: '#EDE7FF' },
  uploaded:  { color: '#1565C0', bg: '#E3F2FD' },
  reviewed:  { color: '#F57F17', bg: '#FFF8E1' },
  status:    { color: '#E65100', bg: '#FFF3E0' },
  note:      { color: '#2E7D32', bg: '#E8F5E9' },
}

// ── Document card ─────────────────────────────────────────────────────────────
function DocumentCard({ doc, onApprove, onReject }: { doc: any; onApprove: () => void; onReject: () => void }) {
  const docStatusConfig: Record<string, { bg: string; color: string }> = {
    Approved: { bg: '#E8F5E9', color: '#2E7D32' },
    Rejected: { bg: '#FFEBEE', color: '#C62828' },
    Pending: { bg: '#FFF8E1', color: '#F57F17' },
  }
  const cfg = docStatusConfig[doc.status] ?? docStatusConfig.Pending
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#EDE7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DocIcon sx={{ fontSize: 18, color: themeColors.primary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>{doc.name}</Typography>
          <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.fileName}</Typography>
        </Box>
        <Chip label={doc.status} size="small" sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, fontSize: '10px', fontWeight: 600, '& .MuiChip-label': { px: '7px' } }} />
      </Box>
      <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>Uploaded: {fmt(doc.uploadDate)}</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
        <Button size="small" startIcon={<ViewIcon sx={{ fontSize: 13 }} />} sx={{ fontSize: '11px', height: 26, textTransform: 'none', borderRadius: '6px', border: `1px solid ${themeColors.border}`, color: themeColors.text.secondary, '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }, flex: 1 }}>View</Button>
        <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />} sx={{ fontSize: '11px', height: 26, textTransform: 'none', borderRadius: '6px', border: `1px solid ${themeColors.border}`, color: themeColors.text.secondary, '&:hover': { borderColor: themeColors.primary, color: themeColors.primary }, flex: 1 }}>Download</Button>
        <Button size="small" onClick={onApprove} sx={{ fontSize: '11px', height: 26, textTransform: 'none', borderRadius: '6px', bgcolor: '#E8F5E9', color: '#2E7D32', '&:hover': { bgcolor: '#C8E6C9' }, flex: 1 }}>Approve</Button>
        <Button size="small" onClick={onReject} sx={{ fontSize: '11px', height: 26, textTransform: 'none', borderRadius: '6px', bgcolor: '#FFEBEE', color: '#C62828', '&:hover': { bgcolor: '#FFCDD2' }, flex: 1 }}>Reject</Button>
      </Box>
    </Paper>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CustomerVerificationDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [noteText, setNoteText] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['customer-verification-detail', id],
    queryFn: () => customerVerificationApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  })

  const [notes, setNotes] = useState<VerificationNote[]>([])
  const allNotes = [...(data?.notes ?? []), ...notes]

  const statusMutation = useMutation({
    mutationFn: (status: CustomerVerificationStatus) => customerVerificationApi.updateStatus(id!, status),
    onSuccess: (_, status) => {
      toast.success(`Status updated to ${status}`)
      queryClient.invalidateQueries({ queryKey: ['customer-verification-detail', id] })
      queryClient.invalidateQueries({ queryKey: ['customer-verifications'] })
    },
    onError: () => toast.error('Failed to update status'),
  })

  const noteMutation = useMutation({
    mutationFn: (content: string) => customerVerificationApi.addNote(id!, content, 'You'),
    onSuccess: (note) => { setNotes(prev => [...prev, note]); setNoteText('') },
    onError: () => toast.error('Failed to add note'),
  })

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}><CircularProgress size={36} /></Box>
  }
  if (!data) {
    return <Box sx={{ textAlign: 'center', py: 8 }}><Typography color="text.secondary">Verification not found.</Typography></Box>
  }

  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh', pb: 5 }}>
      {/* ── Page Header ── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${themeColors.border}`, px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <IconButton size="small" onClick={() => navigate('/customer-verification')} sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '8px', width: 32, height: 32 }}>
            <BackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>
            <Box component="span" sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }} onClick={() => navigate('/customer-verification')}>Customer Verification</Box>
            {' / '}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.primary, fontWeight: 600 }}>{data.customerId}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Avatar sx={{ width: 48, height: 48, fontSize: '16px', fontWeight: 700, bgcolor: avatarColor(data.customerName), borderRadius: '12px' }}>
            {initials(data.customerName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: themeColors.text.primary }}>{data.customerName}</Typography>
              <StatusBadge status={data.status} />
              <Chip label={data.verificationType} size="small"
                sx={{ height: 22, borderRadius: '6px', bgcolor: '#EDE7FF', color: themeColors.primary, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '8px' } }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>{data.customerId}</Typography>
              <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>•</Typography>
              <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>Assigned: <b>{data.assignedVerifier}</b></Typography>
              <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>•</Typography>
              <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>Submitted: {fmt(data.submittedDate)}</Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
              onClick={() => statusMutation.mutate('Approved')}
              disabled={statusMutation.isPending || data.status === 'Approved'}
              sx={{ fontSize: '12px', height: 34, textTransform: 'none', borderRadius: '8px', bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' }, boxShadow: 'none' }}
            >Approve</Button>
            <Button
              variant="contained"
              startIcon={<RejectIcon sx={{ fontSize: 15 }} />}
              onClick={() => statusMutation.mutate('Rejected')}
              disabled={statusMutation.isPending || data.status === 'Rejected'}
              sx={{ fontSize: '12px', height: 34, textTransform: 'none', borderRadius: '8px', bgcolor: '#C62828', '&:hover': { bgcolor: '#B71C1C' }, boxShadow: 'none' }}
            >Reject</Button>
            <Button
              variant="outlined"
              startIcon={<RequestIcon sx={{ fontSize: 15 }} />}
              onClick={() => statusMutation.mutate('Need More Documents')}
              disabled={statusMutation.isPending}
              sx={{ fontSize: '12px', height: 34, textTransform: 'none', borderRadius: '8px', borderColor: '#E65100', color: '#E65100', '&:hover': { borderColor: '#BF360C', bgcolor: '#FFF3E0' } }}
            >Request Documents</Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 3, pt: 2.5 }}>
        <Grid container spacing={2.5}>
          {/* Left column */}
          <Grid component="div" size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              {/* Customer Info */}
              <SectionCard title="Customer Information" icon={<PersonIcon sx={{ fontSize: 16 }} />}>
                <InfoRow icon={<PersonIcon sx={{ fontSize: 15 }} />} label="Customer Name" value={data.customerName} />
                <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />} label="Company Name" value={data.companyName} />
                <InfoRow icon={<EmailIcon sx={{ fontSize: 15 }} />} label="Email" value={data.email} />
                <InfoRow icon={<PhoneIcon sx={{ fontSize: 15 }} />} label="Mobile" value={maskPhoneNumber(data.mobileNumber)} />
                <InfoRow icon={<PhoneIcon sx={{ fontSize: 15 }} />} label="Alternate Mobile" value={maskPhoneNumber(data.alternateMobile)} />
                <InfoRow icon={<LocationIcon sx={{ fontSize: 15 }} />} label="Address" value={data.address} />
                <InfoRow icon={<LocationIcon sx={{ fontSize: 15 }} />} label="City" value={data.city} />
                <InfoRow icon={<LocationIcon sx={{ fontSize: 15 }} />} label="State" value={data.state} />
                <InfoRow icon={<LocationIcon sx={{ fontSize: 15 }} />} label="Country" value={data.country} />
              </SectionCard>

              {/* Business Info */}
              <SectionCard title="Business Information" icon={<BusinessIcon sx={{ fontSize: 16 }} />}>
                <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />} label="Company Name" value={data.companyName} />
                <InfoRow icon={<DocIcon sx={{ fontSize: 15 }} />} label="GST Number" value={data.gstNumber} />
                <InfoRow icon={<DocIcon sx={{ fontSize: 15 }} />} label="PAN Number" value={data.panNumber} />
                <InfoRow icon={<DocIcon sx={{ fontSize: 15 }} />} label="Registration Number" value={data.registrationNumber} />
                <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />} label="Industry Type" value={data.industryType} />
                <InfoRow icon={<EmailIcon sx={{ fontSize: 15 }} />} label="Website" value={data.website} />
              </SectionCard>

              {/* Checklist */}
              <SectionCard title="Verification Checklist" icon={<CheckedIcon sx={{ fontSize: 16 }} />}>
                <Stack spacing={1}>
                  {data.checklist.map(item => (
                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {item.verified
                        ? <CheckedIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                        : <UncheckedIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />}
                      <Typography sx={{ fontSize: '13px', color: item.verified ? themeColors.text.primary : themeColors.text.secondary, fontWeight: item.verified ? 500 : 400 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </SectionCard>
            </Stack>
          </Grid>

          {/* Right column */}
          <Grid component="div" size={{ xs: 12, md: 8 }}>
            <Stack spacing={2}>
              {/* Documents */}
              <SectionCard title="Documents" icon={<DocIcon sx={{ fontSize: 16 }} />}>
                <Grid container spacing={1.5}>
                  {data.documents.map(doc => (
                    <Grid component="div" size={{ xs: 12, sm: 6 }} key={doc.id}>
                      <DocumentCard
                        doc={doc}
                        onApprove={() => toast.success(`${doc.name} approved`)}
                        onReject={() => toast.error(`${doc.name} rejected`)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </SectionCard>

              {/* Timeline */}
              <SectionCard title="Verification Timeline" icon={<TimeIcon sx={{ fontSize: 16 }} />}>
                <Box sx={{ position: 'relative' }}>
                  {data.timeline.map((event, idx) => {
                    const cfg = TL_ICONS[event.type] ?? { color: '#616161', bg: '#F5F5F5' }
                    const isLast = idx === data.timeline.length - 1
                    return (
                      <Box key={event.id} sx={{ display: 'flex', gap: 2, pb: isLast ? 0 : 2.5, position: 'relative' }}>
                        {/* Connector line */}
                        {!isLast && <Box sx={{ position: 'absolute', left: 15, top: 32, bottom: 0, width: 2, bgcolor: '#F0F0F0' }} />}
                        {/* Icon */}
                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                          <TimeIcon sx={{ fontSize: 15, color: cfg.color }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>{event.event}</Typography>
                          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mt: 0.25 }}>{event.description}</Typography>
                          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>{fmtTime(event.timestamp)}</Typography>
                            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>•</Typography>
                            <Typography sx={{ fontSize: '11px', color: themeColors.primary, fontWeight: 600 }}>{event.user}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </SectionCard>

              {/* Internal Notes */}
              <SectionCard title="Internal Notes" icon={<AddIcon sx={{ fontSize: 16 }} />}>
                {/* Note Input */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '11px', fontWeight: 700, bgcolor: themeColors.primary, flexShrink: 0 }}>
                    {initials('You')}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      multiline
                      minRows={2}
                      placeholder="Add an internal note... Use @mention to tag someone"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small" variant="contained"
                        disabled={!noteText.trim() || noteMutation.isPending}
                        onClick={() => noteMutation.mutate(noteText.trim())}
                        sx={{ fontSize: '12px', height: 30, textTransform: 'none', borderRadius: '8px', bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark }, boxShadow: 'none' }}
                      >Add Note</Button>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Notes List */}
                {allNotes.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>No notes yet. Add the first note above.</Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {allNotes.map((note) => (
                      <Box key={note.id} sx={{ display: 'flex', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '11px', fontWeight: 700, bgcolor: avatarColor(note.author), flexShrink: 0 }}>
                          {initials(note.author)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>{note.author}</Typography>
                            <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>{fmtTime(note.timestamp)}</Typography>
                          </Box>
                          <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8F9FC', borderRadius: '10px', border: `1px solid ${themeColors.borderLight}` }}>
                            <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, lineHeight: 1.5 }}>{note.content}</Typography>
                          </Paper>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
